import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChats } from '../../contexts/ChatsContext';

function TrashButton({ onClick, title = 'Delete chat' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="w-8 h-8 rounded-lg flex items-center justify-center text-[#8A8B91] hover:bg-wash hover:text-[#D14343] transition-colors flex-shrink-0"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M3 6h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 6V4.5A2.5 2.5 0 0 1 10.5 2h3A2.5 2.5 0 0 1 16 4.5V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M6.5 6l1 15A2 2 0 0 0 9.5 23h5a2 2 0 0 0 2-2l1-15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M10 11v7M14 11v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function SessionRow({ s, active, onClick, onDelete, time }) {
  return (
    <div
      className="w-full flex items-start gap-3 px-5 py-3.5 border-b border-wash hover:bg-[#FAFAFA] transition-colors"
      style={{ background: active ? '#E9F6F6' : undefined }}
    >
      <button type="button" onClick={onClick} className="flex-1 min-w-0 text-left flex gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#F3F4F6' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 3.5h8M3 6.5h6M3 9.5h8" stroke="#063F5A" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink leading-tight truncate">{s.title || 'Chat'}</div>
          <div className="text-xs text-copy mt-0.5 leading-relaxed truncate">
            {s.preview || '—'}
          </div>
          <div className="text-xs text-muted mt-1">{time}</div>
        </div>
      </button>
      <TrashButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete?.();
        }}
      />
    </div>
  );
}

export function HistoryPanel({ open, onClose }) {
  const nav = useNavigate();
  const { sessions, activeId, setActive, fmtTime, deleteSession, newSession } = useChats();

  const list = useMemo(() => {
    const sorted = [...sessions].sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
    return sorted.slice(0, 12);
  }, [sessions]);

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[90]" onClick={onClose} />
      )}
      <div
        className="fixed top-0 right-0 h-full z-[100] bg-white border-l border-trim flex flex-col transition-transform duration-300"
        style={{
          width: 360,
          transform: open ? 'translateX(0)' : 'translateX(calc(100% + 2px))',
          boxShadow: open ? '-4px 0 24px rgba(4,50,67,.12)' : 'none',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-trim flex-shrink-0">
          <div className="text-sm font-semibold text-ink flex-1">History</div>
          <button
            onClick={() => { newSession({ kind: 'manual' }); onClose?.(); nav('/ace'); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-medium hover:shadow-sm transition-all"
          >
            + New Chat
          </button>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash transition-colors"
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1.5 1.5l10 10M11.5 1.5l-10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {list.length === 0 ? (
            <div className="px-5 py-6 text-sm text-muted">
              No sessions yet.
            </div>
          ) : (
            list.map(s => (
              <SessionRow
                key={s.id}
                s={s}
                active={s.id === activeId}
                time={fmtTime(s.updatedAt || s.createdAt)}
                onClick={() => { setActive(s.id); onClose?.(); }}
                onDelete={() => { deleteSession(s.id); }}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-trim flex-shrink-0">
          <button
            onClick={() => { onClose?.(); nav('/history'); }}
            className="w-full py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash transition-colors"
          >
            View all history
          </button>
        </div>
      </div>
    </>
  );
}

